import type { LensScheduleRun } from '~/hooks/apis/reporting/useLensSchedules';

export function activeScheduleRun(runs: Array<LensScheduleRun>, trigger: 'manual' | 'test') {
  return runs.find((run) => run.trigger === trigger && ['queued', 'running', 'unknown'].includes(run.status));
}

export function runSeverity(run: LensScheduleRun): 'error' | 'warning' | 'success' | 'info' {
  if (run.status === 'failed') {
    return 'error';
  }
  if (run.status === 'unknown' || run.status === 'partial') {
    return 'warning';
  }
  return run.status === 'sent' ? 'success' : 'info';
}

/** Email-service acceptance is not confirmation that the message reached an inbox. */
export function runSummary(run: LensScheduleRun | undefined): string {
  if (!run) {
    return 'Not sent yet.';
  }
  const kind = run.trigger === 'test' ? 'Test' : 'Send';
  const files = run.files.filter((file) => !file.error).length;
  const counts = run.deliveryCounts;
  const accepted = counts ? ` for ${counts.accepted} recipient${counts.accepted === 1 ? '' : 's'}` : '';
  switch (run.status) {
    case 'queued':
      return `${kind} queued…`;
    case 'running':
      return `${kind} in progress…`;
    case 'unknown':
      return `${kind} ${run.runDate}: awaiting email-service confirmation. ${counts ? `${counts.accepted} accepted; ${counts.unknown} awaiting confirmation. ` : ''}This send will not be repeated while its status is uncertain.`;
    case 'sent':
      return `${kind} accepted by email service ${run.runDate}${accepted}: ${files} PDF${files === 1 ? '' : 's'}${run.delivery === 'link' ? ' (as a link, too large to attach)' : ''}.`;
    case 'partial': {
      const failedFiles = run.files.filter((file) => file.error).map((file) => file.label);
      const parts = [`${kind} ${run.runDate}: ${files} of ${run.files.length} PDFs.`];
      if (failedFiles.length) {
        parts.push(`Not made: ${failedFiles.join(', ')}.`);
      }
      if (counts) {
        parts.push(
          `Email service accepted ${counts.accepted}; ${counts.failed} failed; ${counts.unknown} awaiting confirmation.`,
        );
      }
      return parts.join(' ');
    }
    default:
      return `Not sent ${run.runDate}: ${run.error ?? 'the email service did not accept this send'}.`;
  }
}
